// Readers for the redeemed journal's entitlement summary, used by the
// "Your book details" tiles. Every value comes from the API — the figures in the
// design ('8.5" × 11" Hardcover Book', '80 pages', …) are illustrative only.
//
// Shape (per the backend docs / observed responses):
//   maximum-book-pages, book-size, book-cover, print-finish, ebook-status,
//   watch-me-grow, talking-family-tree, milestones, growth-charts,
//   prepaid-shipping, access-pass-months, access-pass-expires-at,
//   extra-page-cents, extra-copy-discount-percent

// Casing isn't guaranteed consistent across these v3 payloads.
const value = (summary, key) => {
  if (!summary || typeof summary !== 'object') return undefined;
  const kebab = key.replace(/_/g, '-');
  const snake = key.replace(/-/g, '_');
  const camel = kebab.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  for (const candidate of [key, kebab, snake, camel]) {
    if (summary[candidate] !== undefined) return summary[candidate];
  }
  return undefined;
};

const humanize = input =>
  String(input)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

// e.g. `8.5" × 11" Hardcover Book`
export const bookFormat = summary => {
  const size = value(summary, 'book-size');
  const cover = value(summary, 'book-cover');
  if (!size && !cover) return null;
  return [size, cover ? `${humanize(cover)} Book` : 'Book'].filter(Boolean).join(' ');
};

export const pageCount = summary => Number(value(summary, 'maximum-book-pages')) || 0;

// The boolean capability flags, in the order the design lists them.
const FEATURE_FLAGS = [
  ['milestones', 'Milestones'],
  ['watch-me-grow', 'Watch Me Grow'],
  ['growth-charts', 'Growth Chart'],
  ['talking-family-tree', 'Talking Family Tree'],
  ['prepaid-shipping', 'Shipping'],
];

export const enabledFeatures = summary =>
  FEATURE_FLAGS.filter(([key]) => value(summary, key) === true).map(([, label]) => label);

// How long the recipient has to add stories. Falls back to deriving whole months
// from the access-pass expiry when the month count isn't given directly.
export const accessPassMonths = summary => {
  const months = Number(value(summary, 'access-pass-months')) || 0;
  if (months) return months;

  const expiresAt = value(summary, 'access-pass-expires-at');
  if (!expiresAt) return 0;
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return 0;
  const monthsLeft = Math.round((expiry - new Date()) / (1000 * 60 * 60 * 24 * 30.44));
  return monthsLeft > 0 ? monthsLeft : 0;
};

export const discountLines = summary => {
  const lines = [];

  // The API may express the extra-page discount as a percentage or as a price in
  // cents; render whichever is present rather than assuming one.
  const pagePercent = value(summary, 'extra-page-discount-percent');
  const pageCents = value(summary, 'extra-page-cents') / 100;
  if (pagePercent) lines.push(`${pagePercent}% off extra pages (if needed)`);
  else if (pageCents) lines.push(`Extra pages $${pageCents.toFixed(2)} each (if needed)`);

  const copyPercent = value(summary, 'extra-copy-discount-percent');
  if (copyPercent) lines.push(`${copyPercent}% off extra book copies`);

  return lines;
};

// Pulls the redeemed certificate + its journal out of the redeem response.
// `patch()` normalizes JSON:API, so both the normalized entities and the raw
// `initial` payload are available; read defensively across the plausible shapes.
export const redemptionFromResponse = response => {
  const initial = response?.data?.initial;
  const certificate = initial?.data;
  const included = initial?.included || [];

  const journalFromRelationship = () => {
    const id = certificate?.relationships?.['child-journal']?.data?.id;
    if (!id) return null;
    return (
      included.find(item => item.id === id && String(item.type || '').includes('child-journal')) ||
      { id }
    );
  };

  const journal =
    journalFromRelationship() ||
    included.find(item => String(item.type || '').includes('child-journal')) ||
    null;

  const attributes = certificate?.attributes || {};

  return {
    certificateId: certificate?.id || null,
    state: attributes.state || attributes['state'] || null,
    giverName: attributes['name-of-giver'] || null,
    journalId: journal?.id || null,
    babyName: journal?.attributes?.['first-name'] || null,
    // The journal's entitlement summary, when the response embeds it.
    entitlements:
      journal?.attributes?.entitlements || attributes.entitlements || {},
  };
};
